import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, BookOpen } from "lucide-react";
import { TheoryView } from "@/components/theory-view";

type Exercise = {
  id: string;
  skillKey?: string | null;
  theory: string | null;
  skill: { name: string } | null;
};

type Session = {
  id: string;
  createdAt: string;
  student: { name: string };
  exercises: Exercise[];
};

/**
 * One printed section per skill, in the order the session met them.
 *
 * Deduped on the theory itself rather than on the skill key: a session serves
 * several questions of one skill, and printing its theory once per question
 * would be the same page four times. Keyed by the skill for the heading, so a
 * child reading the sheet knows which topic each page belongs to.
 */
type Section = {
  skillKey: string;
  title: string;
  theory: string;
};

export function theorySections(exercises: Exercise[], skillKey?: string | null): Section[] {
  const seen = new Set<string>();
  const sections: Section[] = [];

  for (const exercise of exercises) {
    if (!exercise.theory) continue;
    if (skillKey && exercise.skillKey !== skillKey) continue;
    if (seen.has(exercise.theory)) continue;
    seen.add(exercise.theory);

    sections.push({
      skillKey: exercise.skillKey || exercise.id,
      title: exercise.skill?.name || "Teorija",
      theory: exercise.theory,
    });
  }

  return sections;
}

/**
 * Theory as something to read, on paper.
 *
 * Deliberately not the „Teorija" checkbox on the worksheet — that boxes the
 * material into 10pt above each block of questions, which is a footnote to the
 * work. This is the other artifact: the reading a child does *before* the
 * worksheet, or the page a grown-up puts on the table beside it. Same content,
 * a different job, so it is a different sheet.
 */
export function PrintTheorySheet({
  session,
  skillKey,
}: {
  session: Session;
  skillKey?: string | null;
}) {
  const sections = theorySections(session.exercises, skillKey);
  const single = !!skillKey && sections.length > 0;

  return (
    <>
      {/* Print controls — hidden when printing */}
      <div className="no-print mb-6 flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/sessions/${session.id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Teorijos spausdinimo peržiūra</h1>
          <p className="text-muted-foreground">
            {single
              ? "Vienos temos teorija"
              : `Visos šios sesijos temos (${sections.length})`}
          </p>
        </div>
        {/* The way out of a one-topic sheet, and into it: whichever you are
            looking at, the other one is a click away rather than a URL to
            construct. */}
        {single && (
          <Button variant="outline" asChild>
            <Link to={`/sessions/${session.id}/theory/print`}>
              <BookOpen className="mr-2 h-4 w-4" />
              Visos sesijos teorija
            </Link>
          </Button>
        )}
        <Button onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          Spausdinti
        </Button>
      </div>

      {sections.length === 0 ? (
        <div className="no-print text-center py-12">
          <p className="text-muted-foreground">
            Šios sesijos temos teorijos neturi.
          </p>
        </div>
      ) : (
        <div
          className="print-content bg-white p-8 max-w-4xl mx-auto"
          style={{ backgroundColor: "white", color: "black", padding: "0" }}
        >
          {sections.map((section, index) => (
            <section
              key={section.skillKey}
              /* A topic starts on its own page. These are separate readings, and
                 a second one beginning halfway down reads as the first one
                 continuing. */
              className={index > 0 ? "print-page-break" : undefined}
              style={{ marginBottom: "1.5rem" }}
            >
              <div
                style={{
                  borderBottom: "1px solid black",
                  paddingBottom: "0.3rem",
                  marginBottom: "0.6rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: "1rem",
                }}
              >
                <div>
                  <p
                    style={{
                      color: "#666",
                      fontSize: "10pt",
                      marginBottom: "0.1rem",
                    }}
                  >
                    Teorija
                  </p>
                  <h2
                    style={{
                      color: "black",
                      fontSize: "16pt",
                      fontWeight: 700,
                      margin: 0,
                    }}
                  >
                    {section.title}
                  </h2>
                </div>
                <p style={{ color: "#666", fontSize: "10pt", textAlign: "right" }}>
                  {session.student.name}
                  {" · "}
                  {new Date(session.createdAt).toLocaleDateString("lt-LT", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>

              <TheoryView html={section.theory} />
            </section>
          ))}
        </div>
      )}
    </>
  );
}
